import React, { useEffect, useState } from 'react';
import T from 'i18n';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useForm, useFieldArray } from 'react-hook-form';
import { IconButton } from '@mui/material';
import { AddCard, Delete } from '@mui/icons-material';
import Split from 'react-split';
import { Col, Form, Row } from 'react-bootstrap';
import { TextForm } from 'features/common';
import { selectCurrentFolder, selectCurrentCaseId } from 'common/selectors';
import { Modal } from 'features/common/MaterialBasedComponents';
import MoveDocument from './MoveDocument';
import * as actions from '../../redux/actions';
import FileViewer from '../../FileViewer';

const ModalSplit = ({ show, handleClose, handleSave, action, selectedRows }) => {
  const scrollbarStyles = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: transparent;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background-color: #cdcdcd;
      border-radius: 10px;
    }
  `;
  const dispatch = useDispatch();
  const file = selectedRows[selectedRows.length - 1];
  const currentFolder = useSelector(selectCurrentFolder);
  const currentCaseId = useSelector(selectCurrentCaseId);
  const [formIsValid, setFormIsValid] = useState(false);
  const startPage = 1;
  const endPage = file.lastPage && file.startPage ? file.lastPage - file.startPage + 1 : '';
  const fileName = `${file.name} - Split 1`;
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm({
    mode: 'all',
    defaultValues: {
      fileEntries: [
        {
          name: fileName,
          startPage: startPage,
          endPage: endPage,
        },
      ],
      folder: null,
    },
    reValidateMode: 'onChange',
  });
  const formValues = watch();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'fileEntries',
  });

  const handleAddFileEntry = () => {
    // Create a name for the new entry with incremented split number
    const newSplitNumber = fields.length + 1;
    const newFileName = `${file.name} - Split ${newSplitNumber}`;

    // Add a new entry with the auto-generated name
    append(
      {
        name: newFileName,
        startPage: '',
        endPage: '',
      },
      {
        // Focus on the name field of the newly added entry
        shouldFocus: true,
      },
    );
  };

  // First, store the maximum page value as a constant
  const maxDocumentPages =
    file.lastPage && file.startPage ? file.lastPage - file.startPage + 1 : Infinity;

  // Update the useEffect validation function
  useEffect(() => {
    const validateForm = () => {
      if (!formValues.fileEntries || !Array.isArray(formValues.fileEntries)) {
        setFormIsValid(false);
        return false;
      }

      // Check each file entry for required fields and validation errors
      const areEntriesValid = formValues.fileEntries.every((entry, index) => {
        // Check required fields
        const hasRequiredFields =
          entry &&
          entry.name &&
          entry.name.trim() !== '' &&
          entry.startPage &&
          entry.startPage.toString().trim() !== '' &&
          entry.endPage &&
          entry.endPage.toString().trim() !== '';

        if (!hasRequiredFields) return false;

        // Check for validation errors that would trigger custom error messages
        const startPageVal = parseInt(entry.startPage, 10);
        const endPageVal = parseInt(entry.endPage, 10);

        // Check if any page number is less than 1
        const hasNegativePageError =
          isNaN(startPageVal) || isNaN(endPageVal) || startPageVal < 1 || endPageVal < 1;

        // Check if start page is greater than end page
        const hasPageOrderError = startPageVal > endPageVal;

        // Check if end page exceeds document's total pages
        const hasExceedingPageError = endPageVal > maxDocumentPages;

        // Entry is valid only if there are no validation errors
        return !hasNegativePageError && !hasPageOrderError && !hasExceedingPageError;
      });

      const isFolderValid = !!formValues.folder;
      const isValid = areEntriesValid && isFolderValid;

      setFormIsValid(isValid);
      return isValid;
    };

    validateForm();
  }, [formValues, maxDocumentPages]);

  return (
    <>
      <style>{scrollbarStyles}</style>
      <Modal
        show={show}
        title={T.translate('generic.splitFile') + ' - ' + file.id}
        wide={true}
        handleClose={() => {
          handleClose();
        }}
        handleSave={handleSubmit((data) => {
          const splitFiles = data.fileEntries.map((entry) => ({
            name: entry.name,
            startPage: parseInt(entry.startPage, 10),
            endPage: parseInt(entry.endPage, 10),
          }));
          const targetFolderId = data.folder || '';
          dispatch(
            actions.splitDocument({
              caseId: currentCaseId,
              fileId: file.id,
              targetFolderId,
              splitFiles,
            }),
          ).then(() => {
            handleClose();
          });
        })}
        saveTitle={T.translate('generic.splitFile')}
        disableSave={!formIsValid}
        draggableDialog
        fullHeight={true}
      >
        <Split
          sizes={[45, 55]}
          minSize={100}
          expandToMin={false}
          gutterSize={10}
          gutterAlign="center"
          snapOffset={30}
          dragInterval={1}
          direction="horizontal"
          cursor="col-resize"
          className="case-table-and-viewer"
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <div
              style={{
                flex: '1 1 auto',
                overflowY: 'auto',
                overflowX: 'hidden',
                minHeight: 0,
                marginBottom: '8px',
              }}
              className="custom-scrollbar"
            >
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                }}
                style={{ marginRight: '1rem', marginBottom: '1.5rem' }}
              >
                {fields.map((field, index) => (
                  <Form.Group
                    key={field.id}
                    style={{
                      padding: '15px 10px 5px 10px',
                      border: '1px solid #3A3A3F26',
                      borderRadius: '5px',
                      marginBottom: '10px',
                      position: 'relative',
                    }}
                  >
                    {index > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          zIndex: 1,
                        }}
                      >
                        <IconButton
                          onClick={() => remove(index)}
                          sx={{
                            color: '#dc3545',
                            borderRadius: '5px',
                            padding: '4px',
                            '&:hover': {
                              color: '#bb2d3b',
                            },
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </div>
                    )}

                    {/* Form content */}
                    <Row className="align-items-center">
                      <Col>
                        <TextForm
                          {...register(`fileEntries.${index}.name`, {
                            required: true,
                          })}
                          label={T.translate('case.systemName')}
                          placeholder={T.translate('case.namePlaceholder')}
                          errors={errors?.fileEntries?.[index]?.name}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={4} md={3}>
                        <TextForm
                          {...register(`fileEntries.${index}.startPage`, {
                            required: true,
                          })}
                          label={T.translate('case.startPage')}
                          placeholder={T.translate('case.startPage')}
                          type="number"
                          errors={errors?.fileEntries?.[index]?.startPage}
                        />
                      </Col>
                      <Col xs={4} md={3}>
                        <TextForm
                          {...register(`fileEntries.${index}.endPage`, {
                            required: true,
                            validate: (value) => {
                              const startPageInput = parseInt(
                                formValues.fileEntries?.[index]?.startPage,
                                10,
                              );
                              const endPageInput = parseInt(value, 10);
                              return (
                                (!isNaN(startPageInput) &&
                                  !isNaN(endPageInput) &&
                                  endPageInput >= startPageInput) ||
                                T.translate('case.endPageGreaterThanStart')
                              );
                            },
                          })}
                          label={T.translate('case.endPage')}
                          placeholder={T.translate('case.endPage')}
                          type="number"
                          errors={errors?.fileEntries?.[index]?.endPage}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        {(() => {
                          const ErrorMessage = ({ children }) => (
                            <div
                              style={{
                                color: '#dc3545',
                                fontSize: '0.875em',
                                marginTop: '0.25rem',
                              }}
                            >
                              {children}
                            </div>
                          );

                          // Check for negative values first (highest priority)
                          if (
                            (formValues.fileEntries?.[index]?.startPage &&
                              parseInt(formValues.fileEntries?.[index]?.startPage, 10) < 1) ||
                            (formValues.fileEntries?.[index]?.endPage &&
                              parseInt(formValues.fileEntries?.[index]?.endPage, 10) < 1)
                          ) {
                            return (
                              <ErrorMessage>
                                {T.translate('case.lessThanOnePageNumber')}
                              </ErrorMessage>
                            );
                          }

                          // Check if end page exceeds max pages (second priority)
                          if (
                            formValues.fileEntries?.[index]?.endPage &&
                            parseInt(formValues.fileEntries?.[index]?.endPage, 10) >
                              maxDocumentPages
                          ) {
                            return (
                              <ErrorMessage>
                                {T.translate('case.endPageExceedsDocumentPages') ||
                                  `End page cannot exceed ${maxDocumentPages}`}
                              </ErrorMessage>
                            );
                          }

                          // Check if start page > end page (third priority)
                          if (
                            formValues.fileEntries?.[index]?.startPage &&
                            formValues.fileEntries?.[index]?.endPage &&
                            parseInt(formValues.fileEntries?.[index]?.startPage, 10) >
                              parseInt(formValues.fileEntries?.[index]?.endPage, 10)
                          ) {
                            return (
                              <ErrorMessage>
                                {T.translate('case.endPageGreaterThanStart')}
                              </ErrorMessage>
                            );
                          }

                          return null;
                        })()}
                      </Col>
                    </Row>
                  </Form.Group>
                ))}
                <Row className="mt-2">
                  <Col className="d-flex justify-content-end">
                    <IconButton
                      size="small"
                      onClick={handleAddFileEntry}
                      sx={{
                        bgcolor: '#7BA0F4',
                        borderRadius: '5px',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#1565c0',
                        },
                      }}
                    >
                      <span style={{ marginRight: '6px', fontSize: '0.8rem' }}>
                        {T.translate('generic.add').toLocaleUpperCase()}
                      </span>
                      <AddCard fontSize="small" />
                    </IconButton>
                  </Col>
                </Row>
              </Form>
            </div>
            <div
              style={{
                position: 'relative',
                flex: '0 0 auto',
                paddingRight: '1rem',
                width: '100%',
              }}
            >
              <div>
                <MoveDocument
                  control={control}
                  errors={errors}
                  canUnselect={true}
                  currentFolderType={currentFolder.type}
                  menuPosition="auto"
                />
              </div>
            </div>
          </div>
          <div>
            <FileViewer fileId={file.id} isPreviewMode />
          </div>
        </Split>
      </Modal>
    </>
  );
};

export default ModalSplit;
